import React, { useMemo, useState, useRef, useEffect } from "react";
import { Upload, App, Image, Button, Space } from "antd";
import { UploadOutlined, CameraOutlined, CloseOutlined } from "@ant-design/icons";
import { api } from "@/api";
import CameraCapture from "./CameraCapture";

const DEFAULT_TILE = 150;
const GAP = 8;        // внутренний зазор
const ANT_MARGIN = 8; // стандартный margin карточек Upload

/** простая проверка мобильного устройства */
const isMobileUA = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

/** проверка наличия видеоустройств (камера) */
async function checkHasCamera() {
  try {
    if (!navigator.mediaDevices?.enumerateDevices) return false;
    const list = await navigator.mediaDevices.enumerateDevices();
    return list.some((d) => d.kind === "videoinput");
  } catch {
    return false;
  }
}

/** Сжатие перед загрузкой, чтобы снизить нагрузку/объём */
async function shrinkImage(file, maxSide = 1600, quality = 0.85) {
  try {
    if (!/^image\//.test(file.type)) return file;
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = URL.createObjectURL(file);
    });
    const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
    if (scale === 1) return file;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    return new File([blob], file.name.replace(/\.(png|webp)$/i, ".jpg"), { type: "image/jpeg" });
  } catch {
    return file;
  }
}

export default function MaterialImages({ value = [], onChange }) {
  const { message, modal } = App.useApp();

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [dragUid, setDragUid] = useState(null);

  const [cameraOn, setCameraOn] = useState(false);
  const [facing] = useState("environment"); // без внешнего переключателя
  const [hasCamera, setHasCamera] = useState(false);
  const isMobile = isMobileUA();

  // Проверка камеры (для десктоп-ветки)
  useEffect(() => {
    let mounted = true;
    checkHasCamera().then((ok) => mounted && setHasCamera(ok));
    return () => (mounted = false);
  }, []);

  const urls = Array.isArray(value) ? value : [];

  const fileList = useMemo(
    () =>
      urls.map((u, i) => ({
        uid: `img-${i}`,
        status: "done",
        url: u,
        name: (u || "").split("/").pop() || `image-${i + 1}`,
      })),
    [urls]
  );

  /** Единый аплоадер файла на сервер */
  const uploadFile = async (rawFile) => {
    const compact = await shrinkImage(rawFile);
    const fd = new FormData();
    fd.append("file", compact);
    const { data } = await api.post("/uploads/images", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    onChange?.([...urls, data.url]);
    message.success("Фото завантажено");
  };

  /** Выбор файла из галереи/фс */
  const fileInputRef = useRef(null);
  const openGalleryPicker = () => fileInputRef.current?.click();
  const onHiddenFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) await uploadFile(file);
    e.target.value = "";
  };

  /** Кастомный render для Upload карточек */
  const itemRender = (_originNode, file, _list, actions) => {
    const url = file.url;
    return (
      <div
        draggable
        onDragStart={() => setDragUid(file.uid)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (dragUid) moveByUid(dragUid, file.uid);
          setDragUid(null);
        }}
        style={{
          width: "100%",
          height: "100%",
          border: "1px solid #d9d9d9",
          borderRadius: 12,
          background: "#fff",
          position: "relative",
          overflow: "hidden",
          boxSizing: "border-box",
          cursor: "pointer",
        }}
        onClick={() => openPreview(url)}
      >
        <img
          src={url}
          alt={file.name}
          style={{ width: "100%", height: "100%", objectFit: "contain", background: "#f5f5f5" }}
          draggable={false}
        />
        <div
          onClick={async (e) => {
            e.stopPropagation();
            const ok = await confirmRemove(url);
            if (ok) actions?.remove?.();
          }}
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: "rgba(0,0,0,0.5)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            zIndex: 5,
          }}
        >
          <CloseOutlined style={{ fontSize: 12 }} />
        </div>
      </div>
    );
  };

  /** Подтверждение удаления */
  const confirmRemove = (url) =>
    new Promise((resolve) => {
      modal.confirm({
        title: "Видалити фото?",
        okText: "Видалити",
        cancelText: "Скасувати",
        okButtonProps: { danger: true },
        onOk: () => {
          onChange?.(urls.filter((u) => u !== url));
          message.success("Фото видалено");
          resolve(true);
        },
        onCancel: () => resolve(false),
      });
    });

  /** Перестановка изображений по UID */
  const moveByUid = (fromUid, toUid) => {
    const from = fileList.findIndex((f) => f.uid === fromUid);
    const to = fileList.findIndex((f) => f.uid === toUid);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...urls];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange?.(next);
  };

  /** Предпросмотр */
  const openPreview = (url) => {
    const idx = urls.findIndex((u) => u === url);
    setPreviewIndex(idx >= 0 ? idx : 0);
    setPreviewVisible(true);
  };

  // ====== адаптив размеров плитки для Upload ======
  const wrapperRef = useRef(null);
  const [tileSize, setTileSize] = useState(DEFAULT_TILE);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;

    const recompute = (width) => {
      const minForDefaultTwo = 2 * DEFAULT_TILE + ANT_MARGIN + GAP;
      if (width <= minForDefaultTwo + 16) {
        const sizeForTwoCols = Math.floor((width - ANT_MARGIN) / 2) - ANT_MARGIN;
        const next = Math.max(88, Math.min(DEFAULT_TILE, sizeForTwoCols));
        setTileSize(next);
      } else {
        setTileSize(DEFAULT_TILE);
      }
    };

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect?.width || el.clientWidth;
        recompute(w);
      }
    });
    ro.observe(el);
    recompute(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // ====== кнопки вверху: мобильный/десктоп разные (без «перемкнути камеру») ======
  const TopButtons = () => {
    if (isMobile) {
      // Мобильный — всегда 2 кнопки
      return (
        <Space style={{ marginBottom: 8 }}>
          <Button icon={<CameraOutlined />} onClick={() => setCameraOn(true)}>
            Зняти фото
          </Button>
          <Button icon={<UploadOutlined />} onClick={openGalleryPicker}>
            З галереї
          </Button>
        </Space>
      );
    }
    // Десктоп — камера только если обнаружена
    return (
      <Space style={{ marginBottom: 8 }}>
        {hasCamera && (
          <Button icon={<CameraOutlined />} onClick={() => setCameraOn(true)}>
            Зняти фото з веб-камери
          </Button>
        )}
        <Button icon={<UploadOutlined />} onClick={openGalleryPicker}>
          Обрати файл
        </Button>
      </Space>
    );
  };

  return (
    <>
      <TopButtons />

      {/* скрытый input для выбора из галереи/файла */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={onHiddenFileChange}
      />

      <div ref={wrapperRef} style={{ width: "100%" }}>
        {/* встроенная камера (включается кнопкой), без модалки */}
        {cameraOn && (
          <div style={{ marginBottom: 12 }}>
            <CameraCapture
              facing={facing}
              onShot={async (file) => {
                await uploadFile(file);
                setCameraOn(false);
              }}
              onCancel={() => setCameraOn(false)}
              height={isMobile ? 280 : 320}
            />
          </div>
        )}

        {/* Галерея изображений (Upload только как viewer, без общей кнопки «Додати») */}
        <Upload
          rootClassName="mi-grid"
          listType="picture-card"
          multiple
          fileList={fileList}
          onRemove={() => false}
          itemRender={itemRender}
          showUploadList={{ showRemoveIcon: false, showPreviewIcon: false }}
          style={{ "--ant-upload-picture-card-size": `${tileSize}px` }}
        />

        <style>{`
          .mi-grid .ant-upload-list-picture-card-container,
          .mi-grid .ant-upload-list-item {
            width: var(--ant-upload-picture-card-size);
            height: var(--ant-upload-picture-card-size);
            border-radius: 12px;
            overflow: hidden;
          }
        `}</style>
      </div>

      {/* полноэкранный предпросмотр */}
      <div style={{ display: "none" }}>
        <Image.PreviewGroup
          preview={{
            visible: previewVisible,
            current: previewIndex,
            onVisibleChange: setPreviewVisible,
            onChange: setPreviewIndex,
          }}
        >
          {urls.map((u) => (
            <Image key={u} src={u} />
          ))}
        </Image.PreviewGroup>
      </div>
    </>
  );
}
